import { inject, Injectable } from '@angular/core';
import { ArchiveService } from '@softline/application';
import {
  ApiResourceLocation,
  ConnectionHttpService,
  ResponseResult,
  SOFTLINE_SERVICE_HTTP,
} from '@softline/core';
import { EMPTY, lastValueFrom, Observable } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import {
  SOFTLINE_API_TRANSPORTAUFTRAG,
  SOFTLINE_API_TRANSPORTAUFTRAG_DELIVERY_NOTE_ABSCHLUSSTYPEN,
  SOFTLINE_API_TRANSPORTAUFTRAG_DELIVERY_NOTE_CREATE,
  SOFTLINE_API_TRANSPORTAUFTRAG_DELIVERY_NOTE_SEND,
  SOFTLINE_API_TRANSPORTAUFTRAG_DETAILS,
  SOFTLINE_API_TRANSPORTAUFTRAG_STATION_SIGN,
  SOFTLINE_API_TRANSPORTAUFTRAG_UPDATE_KOPFTEXT,
} from '../transport.api';
import { Auftrag } from '../types/auftrag';
import { AuftragDetail } from '../types/auftrag-detail';
import { KopfauBean } from '../types/kopfau-bean';

type UpdateKopftextResponse = ResponseResult<
  KopfauBean & { archiveKey: string; belegvermerk: string; extLfnr: string }
>;

@Injectable()
export class AuftragResourceService {
  private readonly service = inject<ConnectionHttpService>(SOFTLINE_SERVICE_HTTP);
  private readonly archiveService = inject(ArchiveService);

  loadDetails(
    id: string | number
  ): Observable<AuftragDetail & { stationen: KopfauBean[] }> {
    return this.service
      .get<
        AuftragDetail & { stationen: KopfauBean[] }
      >({ path: SOFTLINE_API_TRANSPORTAUFTRAG_DETAILS, pathParams: { id } })
      .pipe(
        map((o) => ({
          ...o,
          stationen: o.stationen.map((p, i) => ({ ...p, idkopfau: id, lfdnr: i + 1 })),
        }))
      );
  }

  complete(auftrag: Auftrag): Observable<Auftrag> {
    return this.service.create<Auftrag, Auftrag>(
      { path: `${SOFTLINE_API_TRANSPORTAUFTRAG}/{{id}}`, pathParams: { id: auftrag.id } },
      auftrag
    );
  }

  async completeStation(id: string | number, station: KopfauBean): Promise<KopfauBean> {
    let result = await lastValueFrom(this.createKopfLf(id, station));
    result = {
      ...result,
      belegvermerk: station?.lieferhinweis ?? result.belegvermerk,
      extLfnr: station.extLfnr,
    };
    await lastValueFrom(this.updateKopftext(id, result?.kopfau?.idkopflf, result));
    if (station.signature) {
      await lastValueFrom(
        this.uploadSignature(id, result?.kopfau?.idkopflf, station.signature)
      );
    }
    await lastValueFrom(this.belegversand(id, result?.kopfau?.idkopflf, result));
    if (station.files) {
      for (const file of station.files) {
        await lastValueFrom(
          this.archiveService.upload({
            archiveKey: result.archiveKey,
            files: [file],
          })
        );
      }
    }
    return result.kopfau;
  }

  async getAbschlussTypen(id: string | number, station: KopfauBean): Promise<any> {
    return lastValueFrom(this.abschlussTypen(id, station));
  }

  private updateKopftext(
    id: string | number,
    idkopflf: number,
    entity: any
  ): Observable<any> {
    const location: ApiResourceLocation = {
      path: SOFTLINE_API_TRANSPORTAUFTRAG_UPDATE_KOPFTEXT,
      pathParams: { id, idkopflf },
    };
    return this.service.update<KopfauBean, UpdateKopftextResponse>(location, entity).pipe(
      map((o) => o.result),
      catchError(() => EMPTY)
    );
  }

  private uploadSignature(
    id: string | number,
    idkopflf: number,
    file: File
  ): Observable<any> {
    return this.service.upload(
      { path: SOFTLINE_API_TRANSPORTAUFTRAG_STATION_SIGN, pathParams: { id, idkopflf } },
      { files: [file], filename: 'signature.png' }
    );
  }

  private belegversand(
    id: string | number,
    idkopflf: number,
    bean: AuftragDetail
  ): Observable<AuftragDetail> {
    return this.service.create(
      {
        path: SOFTLINE_API_TRANSPORTAUFTRAG_DELIVERY_NOTE_SEND,
        pathParams: { id, idkopflf },
      },
      bean
    );
  }

  private createKopfLf(id: string | number, kopfAu: KopfauBean): Observable<any> {
    const bean = {
      id: kopfAu.id,
      belegnummer: kopfAu.belegnummer,
      lieferadressen: kopfAu.lieferadressen,
      belegversandemail: kopfAu.belegversandemail,
      beweaus: kopfAu.beweaus,
      lieferhinweis: kopfAu.lieferhinweis,
      type: kopfAu.type,
    };
    return this.service.create<any, any>(
      { path: SOFTLINE_API_TRANSPORTAUFTRAG_DELIVERY_NOTE_CREATE, pathParams: { id } },
      bean
    );
  }

  private abschlussTypen(id: string | number, kopfAu: KopfauBean): Observable<any> {
    const bean = {
      id: kopfAu.id,
      belegnummer: kopfAu.belegnummer,
      lieferadressen: kopfAu.lieferadressen,
      belegversandemail: kopfAu.belegversandemail,
      beweaus: kopfAu.beweaus,
      lieferhinweis: kopfAu.lieferhinweis,
      type: kopfAu.type,
    };
    return this.service.create<any, any>(
      {
        path: SOFTLINE_API_TRANSPORTAUFTRAG_DELIVERY_NOTE_ABSCHLUSSTYPEN,
        pathParams: { id },
      },
      bean
    );
  }
}
